import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import type { StudentImportCommitResult } from '@repo/shared-types';
import type { ImportClassContext } from '../types';

interface ResultStepProps {
  result: StudentImportCommitResult;
  warningCount: number;
  onClose: () => void;
  /** Fix wave (review I14) — rows the OPERATOR excluded client-side (or a
   *  same-batch duplicate left on its "bỏ qua" default) before the commit
   *  request was even sent — `result.skipped` only counts server `errors`,
   *  so the class-context summary line folds this in too (see
   *  `ImportWizard.handleCommit`). Unused outside class context. */
  excludedCount?: number;
  /** THI-267 Task 13 — set in class context: swaps the generic four-tile
   *  summary for the one-line copy the handoff specifies ("Đã thêm 14 học
   *  viên vào Toán 8, 2 em dùng hồ sơ có sẵn, 1 dòng bỏ qua."). */
  classContext?: ImportClassContext | null;
}

export function ResultStep({
  result, warningCount, excludedCount = 0, onClose, classContext = null,
}: ResultStepProps) {
  const { t } = useTranslation('studentImport');

  // Fix wave (I14) — "N dòng bỏ qua" now folds in every row that did
  // nothing: rows the operator excluded before commit, a class-member
  // outcome of 'skipped' (already a member on every requested slot —
  // `classMemberService.ts`'s own `counts.skipped`, carried per-row on
  // `classMemberRows`), and server errors. `result.skipped` alone
  // (= `errors.length`) undercounted the first two.
  const classSkippedTotal = classContext
    ? excludedCount + (result.classMemberRows?.filter((r) => r.outcome === 'skipped').length ?? 0) + result.errors.length
    : result.skipped;

  return (
    <div className="py-4 space-y-5">
      <div className="flex flex-col items-center text-center gap-2">
        <CheckCircle2 className="h-10 w-10 text-green-500" />
        <p className="text-lg font-semibold">{t('result.title')}</p>
      </div>

      {classContext ? (
        <p className="text-center text-[15px] text-surface-700">
          {t('result.classSummary', {
            count: result.classMembersAdded,
            class: classContext.className,
            linked: result.linkedExisting,
            skipped: classSkippedTotal,
          })}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-md border p-3">
            <div className="text-2xl font-bold text-surface-900">{result.created}</div>
            <div className="text-surface-500">{t('result.created')}</div>
          </div>
          <div className="rounded-md border p-3">
            <div className="text-2xl font-bold text-surface-900">{result.payersCreated}</div>
            <div className="text-surface-500">{t('result.payersCreated')}</div>
          </div>
          <div className="rounded-md border p-3">
            <div className="text-2xl font-bold text-surface-900">{result.payersLinked}</div>
            <div className="text-surface-500">{t('result.payersLinked')}</div>
          </div>
          <div className="rounded-md border p-3">
            <div className="text-2xl font-bold text-surface-900">{result.skipped}</div>
            <div className="text-surface-500">{t('result.skipped')}</div>
          </div>
        </div>
      )}

      {warningCount > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700">
            <AlertTriangle className="h-4 w-4" /> {t('result.warningsTitle', { count: warningCount })}
          </p>
          <p className="text-xs text-amber-700/80 mt-1">{t('result.warningsHint')}</p>
        </div>
      )}

      {result.errors.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm font-medium text-red-700 mb-1.5">
            {t('result.errorsTitle', { count: result.errors.length })}
          </p>
          <ul className="text-xs text-red-600 space-y-0.5 max-h-32 overflow-y-auto">
            {result.errors.map((e, i) => (
              <li key={`${e.rowNum}-${i}`}>{t('result.errorLine', { row: e.rowNum, reason: e.reason })}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end pt-2 border-t">
        <Button onClick={onClose}>{t('result.close')}</Button>
      </div>
    </div>
  );
}
